export type ViewMode = 'month' | 'week';

export interface Period {
  startDate: string;
  endDate: string;
  label: string;
}

export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function formatDisplayDate(dateStr: string): string {
  const date = parseDate(dateStr);
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function getFirstDayOfMonth(year: number, month: number): string {
  return formatDate(new Date(year, month - 1, 1));
}

export function getLastDayOfMonth(year: number, month: number): string {
  return formatDate(new Date(year, month, 0));
}

export function getISOWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

export function getWeekStartEnd(year: number, week: number): { startDate: string; endDate: string } {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const firstMonday = new Date(jan4);
  firstMonday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);

  const start = new Date(firstMonday);
  start.setUTCDate(firstMonday.getUTCDate() + (week - 1) * 7);

  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  return { startDate: formatDate(start), endDate: formatDate(end) };
}

export function getWeeksInYear(year: number): { week: number; label: string; startDate: string; endDate: string }[] {
  const weeks: { week: number; label: string; startDate: string; endDate: string }[] = [];
  // ISO year can have 52 or 53 weeks; generate up to 53
  for (let week = 1; week <= 53; week++) {
    const { startDate, endDate } = getWeekStartEnd(year, week);
    const start = parseDate(startDate);
    // Stop if week starts in next calendar year (except week 1 overlap)
    if (week > 1 && start.getFullYear() > year) break;
    weeks.push({
      week,
      label: `Semana ${week} (${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)})`,
      startDate,
      endDate,
    });
  }
  return weeks;
}

export function getLastCompletedWeek(): Period {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday
  // Last completed week ends on Sunday
  const daysSinceLastSunday = dayOfWeek === 0 ? 7 : dayOfWeek;
  const lastSunday = new Date(today);
  lastSunday.setDate(today.getDate() - daysSinceLastSunday);

  const lastMonday = new Date(lastSunday);
  lastMonday.setDate(lastSunday.getDate() - 6);

  return {
    startDate: formatDate(lastMonday),
    endDate: formatDate(lastSunday),
    label: 'Última semana completada',
  };
}

export function getCurrentMonthPeriod(): Period {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  return {
    startDate: getFirstDayOfMonth(year, month),
    endDate: getLastDayOfMonth(year, month),
    label: 'Mes actual',
  };
}

export function getDefaultPeriodForSeededData(): Period {
  // Seed data ends in May 2026, default to last week of May 2026 for demo
  const { startDate, endDate } = getWeekStartEnd(2026, 22);
  return { startDate, endDate, label: 'Semana 22 de 2026' };
}

// "Acumulado": desde el inicio del mes/año calendario hasta HOY (inclusive).
export function getMonthToDatePeriod(): Period {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  return {
    startDate: getFirstDayOfMonth(year, month),
    endDate: formatDate(today),
    label: `${today.toLocaleDateString('es-CL', { month: 'long' })} ${year} (acumulado)`,
  };
}

export function getYearToDatePeriod(): Period {
  const today = new Date();
  const year = today.getFullYear();
  return {
    startDate: `${year}-01-01`,
    endDate: formatDate(today),
    label: `${year} (acumulado)`,
  };
}
